import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { forceReloadNow, snoozePendingUpdate, useUpdateReloadState } from "../lib/update-reload";

// ═══════════════════════════════════════════════════════════════════════
// UpdateToast — "Agentic Operating System update available · Refresh"
// ═══════════════════════════════════════════════════════════════════════
// register-sw.js no longer hard-reloads on a new service-worker deploy (hostile
// if the user is mid-typing). When the page isn't safe to auto-reload it sets
// window.__sloeSwUpdate and dispatches "sloe:sw-updated"; this toast surfaces
// it and lets the user tap Refresh on their own time. Mounted once in each
// shell root (CodeOSShell = dark, SchemaRenderer = light), themed per surface.
//
// forceShow is for the dev harness only (?__harness=toast).
// ═══════════════════════════════════════════════════════════════════════

declare global {
  interface Window {
    __sloeSwUpdate?: boolean;
  }
}

export function UpdateToast({
  theme = "dark",
  accent = "#7c5cff",
  forceShow = false,
}: {
  theme?: "dark" | "light";
  accent?: string;
  forceShow?: boolean;
}) {
  const updateState = useUpdateReloadState();
  const show = forceShow || updateState.showPrompt;
  const [enter, setEnter] = useState(false);

  // Slide-up on appear.
  useEffect(() => {
    if (!show) {
      setEnter(false);
      return;
    }
    const t = setTimeout(() => setEnter(true), 20);
    return () => clearTimeout(t);
  }, [show]);

  if (!show) return null;
  const dark = theme === "dark";
  const blockingCopy = useMemo(() => {
    if (forceShow || updateState.blockingLabels.length === 0) return null;
    if (updateState.blockingLabels.length === 1) return updateState.blockingLabels[0];
    if (updateState.blockingLabels.length === 2) return `${updateState.blockingLabels[0]} and ${updateState.blockingLabels[1]}`;
    return `${updateState.blockingLabels[0]} and ${updateState.blockingLabels.length - 1} more`;
  }, [forceShow, updateState.blockingLabels]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-label="Agentic Operating System update available"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 px-4 py-6 backdrop-blur-sm pointer-events-none"
    >
      <div
        className={`pointer-events-auto w-full max-w-[420px] rounded-2xl border p-5 shadow-2xl transition-all duration-300 ease-out ${
          enter ? "translate-y-0 scale-100 opacity-100" : "translate-y-4 scale-[0.98] opacity-0"
        } ${
          dark
            ? "bg-[#161616] border-white/12 text-white shadow-black/60"
            : "bg-white border-zinc-200 text-zinc-900 shadow-zinc-900/15"
        }`}
      >
        <div className="flex items-start gap-3.5">
          <div
            className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center"
            style={{ background: `${accent}22`, color: accent }}
          >
            <RefreshCw size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold leading-tight">Your OS has an update ready</div>
            <div className={`text-[13px] leading-relaxed mt-2 ${dark ? "text-white/60" : "text-zinc-500"}`}>
              {forceShow
                ? "Refresh to load the newest bundle."
                : updateState.blocked
                  ? "We’ll reload automatically as soon as you finish this step. You can also reload now when you’re ready."
                  : "A fresh bundle is ready. We’ll reload at the next safe checkpoint unless you choose to do it now."}
            </div>
            {!forceShow && blockingCopy && (
              <div className={`mt-2 text-[11px] font-medium ${dark ? "text-white/45" : "text-zinc-500"}`}>
                Waiting on: {blockingCopy}
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={forceReloadNow}
            className="w-full rounded-xl px-4 py-3 text-[14px] font-semibold text-[#0a0a0a] transition-opacity hover:opacity-90"
            style={{ background: accent }}
          >
            Reload now
          </button>
          {!forceShow && (
            <button
              type="button"
              onClick={snoozePendingUpdate}
              className={`w-full rounded-xl border px-4 py-3 text-[13px] font-semibold transition-colors ${
                dark
                  ? "border-white/12 bg-white/[0.03] text-white/75 hover:bg-white/[0.07]"
                  : "border-zinc-200 bg-zinc-50 text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              Remind me shortly
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
